import {
    type ViewStyle,
    Animated,
    Platform
} from "react-native";
import {
    MOTION_ICON_APPEAR_SCALE,
    MOTION_ICON_APPEAR_BLUR
} from "./timing";

export type MotionProgressType = Animated.Value | Animated.AnimatedInterpolation<number>;

export type MotionStyleType = Animated.WithAnimatedValue<ViewStyle>;

export type MotionAppearStyleConstantType = {
    isReduceMotionEnabled: boolean;
    progress: MotionProgressType;
    fromScale?: number;
};

export type MotionSlideStyleConstantType = {
    isReduceMotionEnabled: boolean;
    progress: MotionProgressType;
    axis?: "x" | "y";
    offset: number;
};

export type MotionCrossfadeStyleConstantType = {
    isReduceMotionEnabled: boolean;
    progress: MotionProgressType;
};

export type MotionCrossfadeStyleType = {
    outgoing: MotionStyleType;
    incoming: MotionStyleType;
};

export const getAppearStyle = ({
    fromScale = MOTION_ICON_APPEAR_SCALE,
    isReduceMotionEnabled,
    progress
}: MotionAppearStyleConstantType): MotionStyleType => {
    const style: MotionStyleType = {
        opacity: progress
    };

    if(isReduceMotionEnabled) {
        return style;
    }

    style.transform = [
        {
            scale: progress.interpolate({
                outputRange: [
                    fromScale,
                    1
                ],
                inputRange: [
                    0,
                    1
                ]
            })
        }
    ];

    if(Platform.OS === "web") {
        Object.assign(style, {
            filter: progress.interpolate({
                outputRange: [
                    `blur(${MOTION_ICON_APPEAR_BLUR}px)`,
                    "blur(0px)"
                ],
                inputRange: [
                    0,
                    1
                ]
            })
        });
    }

    return style;
};

export const getSlideStyle = ({
    isReduceMotionEnabled,
    axis = "y",
    progress,
    offset
}: MotionSlideStyleConstantType): MotionStyleType => {
    const style: MotionStyleType = {
        opacity: progress
    };

    if(isReduceMotionEnabled || !offset) {
        return style;
    }

    const translate = progress.interpolate({
        outputRange: [
            offset,
            0
        ],
        inputRange: [
            0,
            1
        ]
    });

    style.transform = [
        axis === "x" ? {
            translateX: translate
        } : {
            translateY: translate
        }
    ];

    return style;
};

export const getCrossfadeStyle = ({
    isReduceMotionEnabled,
    progress
}: MotionCrossfadeStyleConstantType): MotionCrossfadeStyleType => {
    return {
        outgoing: getAppearStyle({
            progress: progress.interpolate({
                outputRange: [
                    1,
                    0
                ],
                inputRange: [
                    0,
                    1
                ]
            }),
            isReduceMotionEnabled
        }),
        incoming: getAppearStyle({
            isReduceMotionEnabled,
            progress
        })
    };
};
